import type { ReactNode } from 'react';
import { EmptyState, StatusDot } from '@paige/ui';
import { useFormat } from '@paige/format';
import type { AdminPools } from './pools-types';
import { PoolPart } from './PoolPart';
import { ReleaseTimeline } from './ReleaseTimeline';
import { buildWord, summaryOf } from './pools-words';
import '../admin.css';
import '../flow/flow.css';
import './pools.css';

export interface PoolsBoardProps {
  view: AdminPools;
  /** Set when the last refresh failed and this is the picture from before it: what failed, in a sentence. */
  refreshFailed?: string | undefined;
  /** The Retry beside a failed refresh. */
  onRetry?: (() => void) | undefined;
}

/** The answer in one line: is everything up, what is not, the last release, and how fresh this is. */
function Summary({ view, now }: { view: AdminPools; now: number }): ReactNode {
  const f = useFormat();
  const s = summaryOf(view);
  const latest = view.releases[0];
  return (
    <section className={`pl-summary pl-summary--${s.tone}`} aria-label="Summary">
      <p className="pl-summary__head">
        <StatusDot tone={s.tone} label={s.headline} />
        <strong>{s.headline}</strong>
        {s.trouble.length > 0 && !view.stale ? <span>{f.list(s.trouble)}</span> : null}
      </p>
      <dl className="pl-summary__facts">
        <div>
          <dt>{view.stale ? 'Up then' : 'Up'}</dt>
          <dd>
            {f.int(s.up)} of {f.int(s.wanted)}
            {s.off > 0 ? `, ${f.int(s.off)} off` : ''}
          </dd>
        </div>
        <div>
          <dt>Last release</dt>
          <dd>
            {latest ? (
              <>
                {f.ago(latest.at, now)}
                {latest.build ? <code> {buildWord(latest.build)}</code> : null}
              </>
            ) : (
              'none on record'
            )}
          </dd>
        </div>
        <div>
          <dt>Reported</dt>
          <dd>{f.ago(view.reportedAt, now)}</dd>
        </div>
      </dl>
    </section>
  );
}

/** What the picture is not: a refresh that failed (with Retry), or a report that has gone old. */
function Notices({ view, refreshFailed, onRetry }: PoolsBoardProps): ReactNode {
  const f = useFormat();
  return (
    <>
      {refreshFailed ? (
        <p className="pl-notice" role="status">
          <span>Could not refresh: {refreshFailed} Showing the last picture.</span>
          {onRetry ? (
            <button type="button" className="pl-notice__retry" onClick={onRetry}>
              Retry
            </button>
          ) : null}
        </p>
      ) : null}
      {view.stale ? (
        <p className="pl-notice" role="status">
          The health monitor last reported {f.ago(view.reportedAt, Date.parse(view.asOf))}.
          Everything below is as of then.
        </p>
      ) : null}
    </>
  );
}

function Group({
  title,
  note,
  foot,
  children,
}: {
  title: string;
  note: string;
  foot?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="pl-group" aria-label={title}>
      <h2 className="pl-group__title">
        {title} <span>{note}</span>
      </h2>
      {children}
      {foot ? <p className="pl-group__foot">{foot}</p> : null}
    </section>
  );
}

/**
 * Level 0, screen 6: the pools that read documents and the services around them, and the recent releases.
 * Everything on it is what the health monitor last recorded; when that is old the board says so and greys its
 * dots, and before the first report it says that instead of showing an empty table.
 */
export function PoolsBoard(props: PoolsBoardProps): ReactNode {
  const { view } = props;
  const now = Date.parse(view.asOf);
  if (view.reportedAt === null) {
    return (
      <EmptyState
        title="The health monitor has not reported yet"
        description="This fills in within a minute of the health monitor starting. If it stays empty, the monitor is not running or cannot write its record."
      />
    );
  }
  const parts = (list: AdminPools['pools']): ReactNode => (
    <div className="pl-grid">
      {list.map((p) => (
        <PoolPart key={p.id} part={p} now={now} known={!view.stale} />
      ))}
    </div>
  );
  return (
    <div className="pl-board">
      <Notices {...props} />
      <Summary view={view} now={now} />
      <Group
        title="Pools"
        note="what reads the documents"
        foot="Busy is measured from finished asks, about half a minute behind, as on Stages and queues."
      >
        {parts(view.pools)}
      </Group>
      <Group title="Services" note="what runs around them">
        {parts(view.services)}
      </Group>
      <Group title="Releases" note="what changed and when">
        <ReleaseTimeline releases={view.releases} now={now} />
      </Group>
    </div>
  );
}
